import {FormClip} from "../../../types/clipTypes";
import React, {useEffect, useState} from "react";
import Grid from "@mui/material/Grid";
import TextinputField from "../../CommonComponents/TextInputField/TextinputField";
import {useTheme} from "../../../contexts/theme";
import StyledTextarea from "../../CommonComponents/StyledTextarea/StyledTextarea";
import StyledAutocomplete from "../../CommonComponents/StyledAutocomplete/StyledAutocomplete";
import {Rating, Typography} from "@mui/material";
import StyledCheckBoxLabel from "../../CommonComponents/StyledCheckbox/StyledCheckBoxLabel";
import TransparentButton from "../../CommonComponents/TransparentButton/TransparentButton";
import {useVideoContentTagsOptions} from "../../../contexts/VideoContentTagsOptions";
import GameTimeStyledComponent from "../../CommonComponents/GameTimeStyledComponent/GameTimeStyledComponent";

interface ClipFormProps{
    clip: FormClip;
    clipId: string | undefined
    onFieldChange: (field: keyof FormClip, value: any) => void;
    onSubmit: () => void;
    teamOptions: string[];
    periodOptions: string[];
    keyMomentOptions: string[];
    disableSubmitButton?: boolean;
}

const requiredFields = new Set<keyof FormClip>(["title", "key_moment_type", "period"]);

const ClipForm: React.FC<ClipFormProps> = ({clipId, teamOptions, clip, onFieldChange, onSubmit, periodOptions, keyMomentOptions, disableSubmitButton = false}) => {
    const theme = useTheme();
    const {videoContentTagsOptions} = useVideoContentTagsOptions()

    const inputTextStyle = {
        borderBottomColor:theme.palette.orange,
        backgroundColor: 'none',
        color: theme.palette.white,
        borderColor: theme.palette.white,
        borderRadius: theme.border.width.hard,
        height: '40px'
    }

    const [errors, setErrors] = useState<Partial<FormClip>>({});
    const [touched, setTouched] = useState<{ [K in keyof FormClip]?: boolean }>({});

    function isFieldInvalid(field: keyof FormClip, value: any){
        return requiredFields.has(field) && (value === null || (typeof value === "string" && !value.trim()) || value.length === 0)
    }

    const validate = (field: keyof FormClip, value: any) => {
        let error = "";

        if (isFieldInvalid(field, value)) {
            error = "Required Field";
        }

        setErrors((prev) => ({ ...prev, [field]: error }));
    };

    const isValid =
        Object.values(errors).every((err) => !err) &&
        Object.entries(clip).every(
            (entry) => !isFieldInvalid(entry[0] as keyof FormClip, entry[1])
        );


    const handleTextFieldBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const { name } = e.target;
        setTouched((prev) => ({ ...prev, [name]: true }));
        validate(name as keyof FormClip, clip[name as keyof FormClip]);
    };

    const handleAutoCompleteBlur = (name: string) => {
        setTouched((prev) => ({ ...prev, [name]: true }));
        validate(name as keyof FormClip, clip[name as keyof FormClip]);
    };

    const handleAutoCompleteChange = (field: keyof FormClip, value: any) => {
        setTouched((prev) => ({ ...prev, [field]: true }));
        validate(field, value);
        onFieldChange(field, value)
    }

    useEffect(() => {
        setTouched({})
        setErrors({})
    }, [clipId]);

    return (
        <form onSubmit={e => {e.preventDefault(); onSubmit()}} noValidate>
            <Grid container spacing={2}>
                <Grid item lg={12}>
                    <TextinputField
                        width={'100%'}
                        value={clip.title}
                        name={"title"}
                        label={"Title"}
                        placeholder={"Insert clip title"}
                        type={'text'}
                        variant={'outlined'}
                        onChange={(e) => onFieldChange('title', e.target.value)}
                        required={true}
                        onBlur={handleTextFieldBlur}
                        error={touched.title && !!errors.title}
                        helperText={(touched.title && errors.title) as string}
                        onKeyDown={(e) => e.stopPropagation()}
                        {...inputTextStyle}
                    />
                </Grid>
                <Grid item lg={12}>
                    <StyledTextarea
                        width={'100%'}
                        placeholder={'Insert description'}
                        label={'Description'}
                        value={clip.description}
                        onChange={e => onFieldChange('description', e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                    />
                </Grid>
                <Grid item lg={12}>
                    <StyledAutocomplete
                        freeSolo={true}
                        name={'key_moment_type'}
                        label={'Key Moment'}
                        onChange={(_, newValue) => handleAutoCompleteChange('key_moment_type', newValue)}
                        value={clip.key_moment_type}
                        multiple={false}
                        options={keyMomentOptions}
                        required={true}
                        error={touched.key_moment_type && !!errors.key_moment_type}
                        helperText={(touched.key_moment_type && errors.key_moment_type) as string}
                        onBlur={() => handleAutoCompleteBlur('key_moment_type')}
                        onKeyDown={(e) => e.stopPropagation()}
                    />
                </Grid>
                {clip.key_moment_type === 'GOAL' && <Grid item md={12} lg={6}>
                    <StyledCheckBoxLabel
                        label={"Own Goal"}
                        checked={clip.owngoal}
                        height={"20px"}
                        width={"20px"}
                        iconSize={"25px"}
                        onChange={(e) => onFieldChange('owngoal', (e.target as any).checked)}
                        onKeyDown={(e) => e.stopPropagation()}
                    />
                </Grid>}
                {clip.key_moment_type === 'GOAL' && <Grid item md={12} lg={6}>
                    <StyledCheckBoxLabel
                        label={"Penalty"}
                        checked={clip.penalty}
                        height={"20px"}
                        width={"20px"}
                        iconSize={"25px"}
                        onChange={(e) => onFieldChange('penalty', (e.target as any).checked)}
                        onKeyDown={(e) => e.stopPropagation()}
                    />
                </Grid>}
                <Grid item lg={12} >
                    <StyledAutocomplete
                        name={'team'}
                        label={'Team'}
                        onChange={(_, newValue) => onFieldChange('team', newValue)}
                        value={clip.team}
                        multiple={false}
                        options={teamOptions}
                        onKeyDown={(e) => e.stopPropagation()}
                    />
                </Grid>
                <Grid item lg={12}>
                    <TextinputField
                        width={'100%'}
                        value={clip.player}
                        name={"player"}
                        placeholder={"Insert player"}
                        type={'text'}
                        label={'Player'}
                        variant={'outlined'}
                        onChange={(e) => onFieldChange('player', e.target.value)}
                        {...inputTextStyle}
                        onKeyDown={(e) => e.stopPropagation()}
                    />
                </Grid>
                <Grid item lg={12} >
                    <StyledAutocomplete
                        name={'period'}
                        label={'Period'}
                        onChange={(_, newValue) => handleAutoCompleteChange('period', newValue)}
                        value={clip.period}
                        multiple={false}
                        options={periodOptions}
                        required={true}
                        error={touched.period && !!errors.period}
                        helperText={(touched.period && errors.period) as string}
                        onBlur={() => handleAutoCompleteBlur('period')}
                        onKeyDown={(e) => e.stopPropagation()}
                    />
                </Grid>
                <Grid item lg={12} >
                    <GameTimeStyledComponent
                        gameTime={clip.game_time}
                        onChange={(e) => onFieldChange('game_time', e)}/>
                </Grid>
                <Grid item lg={12} >
                    <StyledAutocomplete
                        name={'tags'}
                        label={'Tags'}
                        onChange={(_, newValue) => onFieldChange('tags', newValue)}
                        value={clip.tags}
                        multiple={true}
                        freeSolo={true}
                        options={videoContentTagsOptions}
                        limitTags={3}
                        onKeyDown={(e) => e.stopPropagation()}
                    />
                </Grid>
                <Grid item lg={12} display={'flex'} alignItems={'center'}>
                    <Typography sx={{color: theme.palette.white, fontSize: '20px', paddingRight: theme.spacing.sm}}>Rating:</Typography>
                    <Rating
                        name="read-only"
                        value={clip.manual_rating}
                        precision={0.5}
                        sx={{ fontSize: '20px', "& .MuiRating-iconEmpty": { color: theme.palette.black }, marginRight: theme.spacing.xs }}
                        onChange={(event, newValue) => {
                            if (newValue) onFieldChange('manual_rating', newValue)
                        }}
                        onKeyDown={(e) => e.stopPropagation()}
                    />
                </Grid>
                <Grid lg={12} display={'flex'} alignItems={'center'} justifyContent={'center'}>
                    <TransparentButton
                        type={"submit"}
                        text={"Create Clip"}
                        disabled={disableSubmitButton || !isValid}
                        width={'150px'}
                        onKeyDown={(e) => e.stopPropagation()}
                    />
                </Grid>
            </Grid>
        </form>
    )
}

export default ClipForm